<template>
  
  <div class="p-4 border-t border-gray-200">
    <div class="flex gap-2">
      <div :class="['flex-1', { 'pulse-glow': isMounted && shouldGlow }]" class="chat-input-glow">
        <UTextarea
          ref="textareaRef"
          v-model="newMessage"
          placeholder="Nachricht eingeben..."
          :rows="1"
          autoresize
          @keydown.enter.prevent="sendMessage"
          class="w-full"
        />
      </div>
      <div class="flex items-end gap-2">
        <UButton
          icon="i-lucide-paperclip"
          color="gray"
          variant="ghost"
          size="sm"
          @click="$refs.fileInput.click()"
        />
        <input 
          type="file" 
          multiple 
          @change="handleFileSelect"
          class="hidden"
          ref="fileInput"
        />
        <UButton
          icon="i-lucide-send"
          color="primary"
          @click="sendMessage"
          :disabled="!newMessage.trim()"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UButton } from '#components'
import { collapseTextChangeRangesAcrossMultipleVersions } from 'typescript'
import { ref, watch, nextTick, onMounted } from 'vue'
import { ChatMessage } from '~/types/models'

const props = defineProps({
  messages: {
    type: Array,
    required: true
  },
  isTyping: {
    type: Boolean,
    default: false
  },
  focusInput: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:isTyping', 'upload-file', 'input-focused'])

const newMessage = ref('')
const fileInput = ref(null)
const textareaRef = ref(null)
const shouldGlow = ref(false)
const isMounted = ref(false)

const authStore = ref(useAuthStore());

onMounted(() => {
  isMounted.value = true
})

// Watch for focusInput prop change
watch(() => props.focusInput, (newValue) => {
  console.log(`focusInput changed to ${newValue}`);
  if (newValue && isMounted.value) {
    nextTick(() => {
      // Focus the textarea
      if (textareaRef.value?.$el) {
        const textarea = textareaRef.value.$el.querySelector('textarea')
        if (textarea) {
          textarea.focus()
        }
      }
      
      // Start glow animation (2 pulses)
      shouldGlow.value = true
      
      // Stop glow after animation completes (1s total for 2 pulses)
      setTimeout(() => {
        shouldGlow.value = false
      }, 1000)
      
      emit('input-focused')
    })
  }
})

const handleFileSelect = (e) => {
  const files = Array.from(e.target.files)
  if (files.length > 0) {
    emit('upload-file', files)
    // Add a message about file upload
    const fileMessage = {
      id: Date.now(),
      role: 'user',
      content: `${files.length} Datei${files.length > 1 ? 'en' : ''} hochgeladen: ${files.map(f => f.name).join(', ')}`,
      timestamp: new Date()
    }
    props.messages.push(fileMessage)
    
    // Clear the input
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

const sendMessage = async () => {
  if (!newMessage.value.trim()) return

  const projectsStore = useProjectsStore()
  const authStore = useAuthStore()
  const appStore = useAppStore()
  const auth = useAuth()
  
  // Ensure user is authenticated (create anonymous account if needed)
  if (!authStore.currentUser) {
    console.log("getting temporary account");
    await auth.getTemporaryAccount()
  }
  
  const selectedProject = projectsStore.selectedProject
  const currentUser = authStore.currentUser
  
  if (!selectedProject || !currentUser) {
    console.error('Missing project or user:', { selectedProject: !!selectedProject, currentUser: !!currentUser })
    return
  }

  // Add user message
  const userMessage = new ChatMessage({
    id: `msg-${Date.now()}`,
    userId: currentUser.id,
    role: 'user',
    content: newMessage.value,
    timestamp: new Date()
  })
  
  projectsStore.addChatMessage(selectedProject.id, currentUser.id, userMessage)
  appStore.setTyping(true)

  // Clear input
  const messageContent = newMessage.value
  newMessage.value = ''

  // Call Firebase function to process message
  try {
    const { processChatMessage } = useFirebaseFunctions()
    
    const response = await processChatMessage({
      message: messageContent,
      projectId: selectedProject.id,
      userId: currentUser.id,
      timestamp: new Date().toISOString()
    })
    
    const assistantMessage = new ChatMessage({
      id: `msg-${Date.now()}-assistant`,
      userId: 'assistant',
      role: 'assistant',
      content: response.response || 'Entschuldigung, es gab einen Fehler bei der Verarbeitung.',
      timestamp: new Date()
    })
    
    projectsStore.addChatMessage(selectedProject.id, currentUser.id, assistantMessage)
    appStore.setTyping(false)
  } catch (error) {
    console.error('Error calling Firebase function:', error)
    
    // Fallback to dummy response
    const assistantMessage = new ChatMessage({
      id: `msg-${Date.now()}-assistant`,
      userId: 'assistant',
      role: 'assistant',
      content: 'Entschuldigung, der Chat-Service ist momentan nicht verfügbar.',
      timestamp: new Date()
    })
    
    projectsStore.addChatMessage(selectedProject.id, currentUser.id, assistantMessage)
    appStore.setTyping(false)
  }
}





</script>

<style scoped>
.chat-input-glow {
  animation: pulse-glow 1s ease-in-out;
  border-radius: 0.375rem;
}

.pulse-glow {
    box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.4), 0 0 30px rgba(234, 179, 8, 0.3);
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(234, 179, 8, 0);
  }
  25% {
    box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.4), 0 0 30px rgba(234, 179, 8, 0.3);
  }
  50% {
    box-shadow: 0 0 0 0 rgba(234, 179, 8, 0);
  }
  75% {
    box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.4), 0 0 30px rgba(234, 179, 8, 0.3);
  }
}
</style>